import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import { useUserStore } from '../../zustand/UserStore';
import { useHierarchicalCallsStore } from '../../zustand/HierarchicalCallsStore';
import { useNavigate } from 'react-router-dom';

export default function CallsTable() {
  const isDarkMode = useTheme((state) => state.theme);
  const user = useUserStore((state) => state.user);
  const { 
    calls, 
    pagination, 
    isLoading, 
    searchQuery, 
    fetchCalls, 
    setSearchQuery 
  } = useHierarchicalCallsStore();
  const nav = useNavigate();
  
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  // Get pagination values from store
  const { currentPage, totalPages, totalCount, hasNext, hasPrev } = pagination;
  const perPage = 10;

  // Initial fetch when user becomes available
  useEffect(() => {
    if (user) {
      fetchCalls(1, '');
    }
  }, [user, fetchCalls]);

  // Handle search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchQuery !== searchQuery) {
        setSearchQuery(localSearchQuery);
        if (user) {
          fetchCalls(1, localSearchQuery);
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [localSearchQuery, searchQuery, user, fetchCalls, setSearchQuery]);

  const getScoreColor = (score: number): string => {
    if (score >= 8) return '#1a9f6b';
    if (score >= 6) return '#fbbf24';
    return '#ef4444';
  };

  return (
    <div className={`rounded-xl p-6 transition-all duration-300 ${
      isDarkMode 
        ? 'bg-[#0a1414] border border-[#1a2424]' 
        : 'bg-white border border-gray-200 shadow-sm'
    }`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h3 className={`text-xl font-bold ${
          isDarkMode ? 'text-white' : 'text-[#053333]'
        }`}>
          Latest Call Recordings
        </h3>
        <div className="flex gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search calls..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className={`w-full sm:w-64 pl-10 pr-4 py-2 rounded-lg outline-none transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] ${
                isDarkMode 
                  ? 'bg-[#050a0a] text-white border-2 border-[#1a2424] placeholder-gray-500' 
                  : 'bg-gray-100 text-[#053333] border-2 border-gray-300 placeholder-gray-400'
              }`}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`h-12 rounded ${
                isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-200'
              }`}></div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Calls Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <th className="pb-3 font-medium">Representative</th>
                  <th className="pb-3 font-medium">Customer</th>
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Duration</th>
                  <th className="pb-3 font-medium">Score</th>
                  <th className="pb-3 font-medium text-end pr-14">Action</th>
                </tr>
              </thead>
              <tbody className={`text-sm ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>
                {calls.map((call) => (
                  <tr key={call.id} className={`border-t transition-colors duration-300 ${
                    isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
                  }`}>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-[#1a9f6b] text-white">
                          {call.repAvatar}
                        </div>
                        <span>{call.rep}</span>
                      </div>
                    </td>
                    <td className="py-3">{call.customer}</td>
                    <td className="py-3">
                      <div>
                        <div>{call.date}</div>
                      </div>
                    </td>
                    <td className="py-3">{call.duration}</td>
                    <td className="py-3">
                      <span 
                        className={`font-semibold px-2 py-1 rounded ${
                          isDarkMode ? 'bg-[#1a9f6b]/20' : 'bg-[#1a9f6b]/10'
                        }`}
                        style={{ color: getScoreColor(call.score) }}
                      >
                        {call.score}/10
                      </span>
                    </td>
                    <td className="py-3 text-end pr-4">
                      <button
                        onClick={() => nav(`/analysis/${call.id}`)}
                        className="px-4 py-2 cursor-pointer rounded-lg font-semibold transition-all duration-300 text-sm bg-transparent text-[#1a9f6b] border-2 border-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white hover:translate-y-[-1px] hover:shadow-lg"
                        title="View in detail"
                      >
                        View in detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6">
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Showing {((currentPage - 1) * perPage) + 1}-{Math.min(currentPage * perPage, totalCount)} of {totalCount} calls
            </p>
            <div className="flex gap-2">
              <button 
                onClick={() => fetchCalls(currentPage - 1)}
                disabled={!hasPrev}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  !hasPrev 
                    ? (isDarkMode ? 'bg-[#050a0a] text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                    : (isDarkMode 
                        ? 'bg-[#050a0a] text-gray-300 border border-[#1a2424] hover:bg-[#0f1919]' 
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200')
                }`}
              >
                Previous
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => fetchCalls(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm transition-all duration-300 ${
                      pageNum === currentPage
                        ? 'bg-[#1a9f6b] text-white hover:bg-[#148856]'
                        : (isDarkMode 
                            ? 'bg-[#050a0a] text-gray-300 border border-[#1a2424] hover:bg-[#0f1919]' 
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200')
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button 
                onClick={() => fetchCalls(currentPage + 1)}
                disabled={!hasNext}
                className={`px-3 py-1 rounded-lg text-sm transition-all duration-300 ${
                  !hasNext 
                    ? (isDarkMode ? 'bg-[#050a0a] text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                    : (isDarkMode 
                        ? 'bg-[#050a0a] text-gray-300 border border-[#1a2424] hover:bg-[#0f1919]' 
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200')
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
